/**
 * TBN Add-to-Cart Acknowledgment
 * --------------------------------------------------------------
 * Gates Add to Cart / Buy It Now behind an acknowledgment popup for
 * products that have the `custom.atc_ack_message` metafield set.
 *
 * Flow:
 *   1. Shopper clicks Add to Cart (or Buy It Now / presses Enter).
 *   2. Capture-phase listener stops the theme's ajax handler and opens
 *      the popup with the metafield copy (config lives in a
 *      <script data-tbn-ack-config> inside the product form, rendered by
 *      snippets/tbn-ack-data.liquid).
 *   3. On confirm: a hidden `properties[<key>]` input is appended to the
 *      form (so the acknowledgment travels with the line item to the cart,
 *      checkout, order and admin) and the original click is replayed.
 *   4. Mini-cart / cart rows showing that property are restyled into an
 *      amber reminder notice (cart page renders its own notice in Liquid via
 *      snippets/tbn-ack-cart-notice.liquid).
 *
 * Fail-open: if this script errors or JS is disabled, Add to Cart still works.
 *
 * Events (document): tbn-ack:open, tbn-ack:confirm, tbn-ack:cancel
 * dataLayer:          tbn_ack_open, tbn_ack_confirm, tbn_ack_cancel
 */
(function () {
  'use strict';

  var S = window.tbnAckSettings || {};
  var KEY = S.propertyKey || 'Acknowledged';
  var SUBLINE = S.cartSubline || 'You acknowledged this policy on the product page.';
  var SESSION_PREFIX = 'tbnAck:';
  var FORM_SELECTOR = 'form[action*="/cart/add"]';
  var TRIGGER_SELECTOR = '.add_to_cart, .shopify-payment-button, button[name="add"], input[name="add"]';

  var modal = null;
  var state = null;
  var lastFocus = null;

  /* ---------------------------------------------------------- helpers */

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent('tbn-ack:' + name, { detail: detail || {} }));
    } catch (e) { /* noop */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'tbn_ack_' + name }, detail || {}));
    } catch (e) { /* noop */ }
  }

  function getConfig(form) {
    if (!form) return null;
    var el = form.querySelector('[data-tbn-ack-config]');
    if (!el) return null;
    if (el.__tbnCfg !== undefined) return el.__tbnCfg;
    try {
      el.__tbnCfg = JSON.parse(el.textContent);
    } catch (e) {
      el.__tbnCfg = null;
    }
    return el.__tbnCfg;
  }

  function isRemembered(cfg) {
    if (cfg.remember !== 'session') return false;
    try {
      return sessionStorage.getItem(SESSION_PREFIX + cfg.productId) === '1';
    } catch (e) {
      return false;
    }
  }

  function remember(cfg) {
    if (cfg.remember !== 'session') return;
    try {
      sessionStorage.setItem(SESSION_PREFIX + cfg.productId, '1');
    } catch (e) { /* noop */ }
  }

  function ensureProperty(form, cfg) {
    var input = form.querySelector('input[data-tbn-ack-prop]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-tbn-ack-prop', '');
      form.appendChild(input);
    }
    input.name = 'properties[' + KEY + ']';
    input.value = cfg.checkbox || 'Yes';
  }

  function removeProperty(form) {
    var input = form.querySelector('input[data-tbn-ack-prop]');
    if (input) input.parentNode.removeChild(input);
  }

  function markDone(form, cfg) {
    form.setAttribute('data-tbn-ack-done', '1');
    ensureProperty(form, cfg);
  }

  function clearDone(form) {
    form.removeAttribute('data-tbn-ack-done');
    removeProperty(form);
  }

  /* Pre-arm forms whose product was already acknowledged this session. */
  function armForms(root) {
    var forms = (root || document).querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) {
      var cfg = getConfig(forms[i]);
      if (cfg && isRemembered(cfg)) markDone(forms[i], cfg);
    }
  }

  /* ------------------------------------------------------------ modal */

  function buildModal() {
    modal = document.createElement('div');
    modal.className = 'tbn-ack';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="tbn-ack__overlay" data-tbn-ack-cancel></div>' +
      '<div class="tbn-ack__dialog" role="dialog" aria-modal="true" aria-labelledby="tbn-ack-title" aria-describedby="tbn-ack-body">' +
        '<button type="button" class="tbn-ack__close" aria-label="Close" data-tbn-ack-cancel>' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>' +
        '</button>' +
        '<div class="tbn-ack__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="52" height="52"><path d="M3 10.5v3a1.5 1.5 0 0 0 1.5 1.5H7l7.5 4V5L7 9H4.5A1.5 1.5 0 0 0 3 10.5z" fill="#fbbf24" stroke="#111" stroke-width="1.4" stroke-linejoin="round"/><path d="M7.5 15v3.5A1.5 1.5 0 0 0 9 20h1a1.5 1.5 0 0 0 1.5-1.5V17" fill="none" stroke="#111" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.5 9.5a3.5 3.5 0 0 1 0 5M20 7a7 7 0 0 1 0 10" fill="none" stroke="#111" stroke-width="1.4" stroke-linecap="round"/></svg>' +
        '</div>' +
        '<h2 class="tbn-ack__title" id="tbn-ack-title"></h2>' +
        '<div class="tbn-ack__body" id="tbn-ack-body"></div>' +
        '<label class="tbn-ack__check">' +
          '<input type="checkbox" class="tbn-ack__checkbox">' +
          '<span class="tbn-ack__check-box" aria-hidden="true"><svg viewBox="0 0 16 16" width="14" height="14"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg></span>' +
          '<span class="tbn-ack__check-label"></span>' +
        '</label>' +
        '<div class="tbn-ack__actions">' +
          '<button type="button" class="tbn-ack__confirm global-button global-button--primary" data-tbn-ack-confirm></button>' +
          '<button type="button" class="tbn-ack__cancel" data-tbn-ack-cancel></button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-tbn-ack-cancel]')) {
        e.preventDefault();
        cancel();
      } else if (t.closest('[data-tbn-ack-confirm]')) {
        e.preventDefault();
        confirm();
      }
    });

    modal.querySelector('.tbn-ack__checkbox').addEventListener('change', syncConfirmState);

    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key === 'Tab') trapFocus(e);
    });
  }

  function focusables() {
    return Array.prototype.filter.call(
      modal.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.offsetParent !== null; }
    );
  }

  function trapFocus(e) {
    var els = focusables();
    if (!els.length) return;
    var first = els[0];
    var last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function syncConfirmState() {
    if (!state) return;
    var cb = modal.querySelector('.tbn-ack__checkbox');
    var btn = modal.querySelector('[data-tbn-ack-confirm]');
    var ok = !state.cfg.requireCheckbox || cb.checked;
    btn.disabled = !ok;
    btn.classList.toggle('disabled', !ok);
  }

  function openModal(form, cfg, clickEl) {
    if (!modal) buildModal();
    state = { form: form, cfg: cfg, clickEl: clickEl };

    modal.querySelector('.tbn-ack__title').textContent = cfg.title || 'Important – Please Read';
    modal.querySelector('.tbn-ack__body').innerHTML = cfg.body || '';
    modal.querySelector('.tbn-ack__check-label').textContent = cfg.checkbox || 'I understand.';
    modal.querySelector('[data-tbn-ack-confirm]').textContent = cfg.confirmLabel || 'Confirm & Add to Cart';
    modal.querySelector('.tbn-ack__cancel').textContent = cfg.cancelLabel || 'Cancel';

    var check = modal.querySelector('.tbn-ack__check');
    var cb = modal.querySelector('.tbn-ack__checkbox');
    cb.checked = false;
    check.hidden = !cfg.requireCheckbox && cfg.hideCheckbox === true;
    syncConfirmState();

    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('tbn-ack-open');
    // Let the browser paint before focusing so the fade-in is not skipped.
    window.setTimeout(function () {
      var f = focusables();
      if (f.length) (cfg.requireCheckbox ? cb : f[0]).focus();
    }, 20);

    emit('open', { product_id: cfg.productId });
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('tbn-ack-open');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (e) { /* noop */ }
    }
  }

  function cancel() {
    var cfg = state && state.cfg;
    closeModal();
    if (cfg) emit('cancel', { product_id: cfg.productId });
    state = null;
  }

  function confirm() {
    if (!state) return;
    var form = state.form;
    var cfg = state.cfg;
    var clickEl = state.clickEl;
    if (cfg.requireCheckbox && !modal.querySelector('.tbn-ack__checkbox').checked) return;

    markDone(form, cfg);
    remember(cfg);
    closeModal();
    emit('confirm', { product_id: cfg.productId });
    state = null;

    // Replay the original action now that the form is armed.
    window.setTimeout(function () {
      if (clickEl && typeof clickEl.click === 'function') {
        clickEl.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
      // "Ask every time": disarm after the request has been serialized.
      if (cfg.remember !== 'session') {
        window.setTimeout(function () { clearDone(form); }, 1500);
      }
    }, 0);
  }

  /* ------------------------------------------------------------- gate */

  function shouldGate(form) {
    var cfg = getConfig(form);
    if (!cfg) return null;
    if (form.getAttribute('data-tbn-ack-done') === '1') return null;
    if (isRemembered(cfg)) {
      markDone(form, cfg);
      return null;
    }
    return cfg;
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    var trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    var form = trigger.closest(FORM_SELECTOR);
    if (!form) return;
    if (trigger.matches('.add_to_cart') && (trigger.disabled || trigger.classList.contains('disabled'))) return;

    var cfg = shouldGate(form);
    if (!cfg) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    var clickEl = target.closest('button, [role="button"], a, input[type="submit"]') || trigger;
    openModal(form, cfg, clickEl);
  }, true);

  // Enter key in the quantity field submits the form without a click.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    var cfg = shouldGate(form);
    if (!cfg) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
    var btn = form.querySelector('.add_to_cart, button[name="add"]');
    openModal(form, cfg, btn || form);
  }, true);

  /* --------------------------------------------- cart reminder styling */

  function noticeNode(value, subline) {
    var wrap = document.createElement('div');
    wrap.className = 'tbn-ack-notice tbn-ack-notice--inline';
    wrap.innerHTML =
      '<span class="tbn-ack-notice__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 10.5v3a1.5 1.5 0 0 0 1.5 1.5H7l7.5 4V5L7 9H4.5A1.5 1.5 0 0 0 3 10.5z" fill="#fbbf24" stroke="#111" stroke-width="1.6" stroke-linejoin="round"/><path d="M7.5 15v3.5A1.5 1.5 0 0 0 9 20h1a1.5 1.5 0 0 0 1.5-1.5V17" fill="none" stroke="#111" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.5 9.5a3.5 3.5 0 0 1 0 5M20 7a7 7 0 0 1 0 10" fill="none" stroke="#111" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '</span>' +
      '<span class="tbn-ack-notice__text"><strong class="tbn-ack-notice__title"></strong><span class="tbn-ack-notice__sub"></span></span>';
    wrap.querySelector('.tbn-ack-notice__title').textContent = value;
    wrap.querySelector('.tbn-ack-notice__sub').textContent = subline;
    return wrap;
  }

  /* Turn "Acknowledged: <text>" property rows (mini-cart, JS-rendered) into notices. */
  function decorate(root) {
    var rows = (root || document).querySelectorAll('[data-cart-item] .line-item:not(.tbn-ack-line)');
    for (var i = 0; i < rows.length; i++) {
      var el = rows[i];
      var text = (el.textContent || '').trim();
      if (text.indexOf(KEY + ':') !== 0) continue;
      var value = text.slice(KEY.length + 1).trim();
      el.classList.add('tbn-ack-line');
      el.textContent = '';
      el.appendChild(noticeNode(value, SUBLINE));
    }
  }

  var scheduled = false;
  function scheduleDecorate() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      decorate();
      armForms();
    });
  }

  function init() {
    armForms();
    decorate();
    if ('MutationObserver' in window) {
      new MutationObserver(scheduleDecorate).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Small public surface for debugging / other scripts.
  window.tbnAck = {
    open: function (form) {
      var cfg = getConfig(form);
      if (cfg) openModal(form, cfg, form.querySelector('.add_to_cart'));
    },
    decorate: decorate,
    reset: function (productId) {
      try { sessionStorage.removeItem(SESSION_PREFIX + productId); } catch (e) { /* noop */ }
    }
  };
})();
